import React from 'react';
import { ActionMenu, IconButton, TableColumnType } from '@fullstory/components';
import { RowAction } from './types';

/**
 * Hook to create a column for the individual row action menus.
 * This is for the BulkActionTable to use internally.
 */
export function useRowActionColumn<R>(
  rowActions: RowAction<R>[],
  testId: string,
  fixed: boolean,
  resetSelection: () => void,
) {
  if (rowActions.length === 0) {
    return null;
  }

  const column: TableColumnType<R> = {
    key: 'rowActions',
    // If we are using a fixed table, than we need to set a width or else the action button will clip outside the table.
    width: fixed ? 60 : 0,
    // eslint-disable-next-line react/display-name
    renderCell: row => {
      if (rowActions.length === 1 && rowActions[0]?.iconName) {
        if (rowActions[0].disabled?.(row)) {
          return null;
        }
        return (
          <IconButton
            title={rowActions[0]?.label}
            iconName={rowActions[0].iconName}
            onClick={e => {
              e.stopPropagation();
              rowActions[0]?.action(row);
              resetSelection();
            }}
          />
        );
      }
      return (
        /**
         * This div captures the click event and prevents propagation to the table onRow click method. It's not directly interactable
         * hence the disabling of the eslint rules.
         */
        // eslint-disable-next-line jsx-a11y/click-events-have-key-events, jsx-a11y/no-static-element-interactions
        <div onClick={e => e.stopPropagation()}>
          <ActionMenu
            testId={`${testId}-action-menu`}
            onSelect={actionId => {
              const action = rowActions.find(r => r.id === actionId);
              if (action) {
                action.action(row);
                resetSelection();
              }
            }}
            actions={rowActions.map(({ disabled, tooltip, ...rest }) => ({
              ...rest,
              disabled: disabled?.(row),
              tooltip: tooltip?.(row),
            }))}
          />
        </div>
      );
    },
  };
  return column;
}
